import { NeonColors, neonPanel, neonText, neonButton } from '../neon-theme'
import { mountToAppShell } from '../appShell'

interface PlayerSummary {
  id: string
  username: string | null
  firstName: string | null
  telegramId: string | number
  balance: number
  createdAt: string
}

interface RecentPlayerSummary extends PlayerSummary {
  gamesPlayed: number
}

interface PlayerTransaction {
  id: string
  type: string
  amount: number
  description: string | null
  createdAt: string
}

interface PlayerGame {
  id: string
  createdAt: string
  duration: number | null
}

interface PlayerDetail extends PlayerSummary {
  lastName: string | null
  freeGamesLeft: number
  gamesPlayed: number
  evacuations: number
  kills: number
  deaths: number
  transactions: PlayerTransaction[]
  games: PlayerGame[]
}

interface FlagStatus {
  flagged: boolean
  reason: string
}

interface BanRecord {
  id: string
  reason: string
  expiresAt: string | null
  liftedAt: string | null
  createdAt: string
}

interface ViolationRecord {
  type: string
  data: unknown
  createdAt: string
}

interface FinancialRecord {
  type: string
  amount: number
  createdAt: string
}

interface PlayerHistory {
  violations: ViolationRecord[]
  financials: FinancialRecord[]
  bans: BanRecord[]
}

const INPUT_STYLE = `
  background: ${NeonColors.bgDark};
  border: 1px solid ${NeonColors.textDim};
  color: ${NeonColors.textBright};
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 0.85rem;
  box-sizing: border-box;
`

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    })
  } catch {
    return '---'
  }
}

function formatDateTime(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return '---'
  }
}

function txTypeColor(type: string): string {
  switch (type) {
    case 'DEPOSIT': return NeonColors.gold
    case 'WITHDRAW': return NeonColors.success
    case 'GAME_STAKE': return NeonColors.danger
    case 'GAME_EVACUATION': return NeonColors.success
    case 'GAME_WIN': return NeonColors.gold
    default: return NeonColors.textMuted
  }
}

export class AdminPlayerManager {
  private container: HTMLDivElement
  private token: string
  private currentView: 'search' | 'detail' = 'search'
  private selectedPlayerId: string | null = null

  constructor(token: string) {
    this.token = token
    this.container = document.createElement('div')
    this.container.style.cssText = 'width: 100%;'
  }

  render(parent: HTMLElement): void {
    parent.appendChild(this.container)
    this.renderSearchView()
  }

  dispose(): void {
    this.container.remove()
  }

  private renderSearchView(): void {
    this.currentView = 'search'
    this.container.innerHTML = `
      <div style="display: flex; flex-direction: column; gap: 14px;">
        <div style="display: flex; gap: 8px; align-items: center;">
          <input
            type="text"
            id="player-search-input"
            placeholder="Search by @username, name, or full Telegram ID..."
            style="${INPUT_STYLE} flex: 1;"
          />
          <button id="player-search-btn" style="
            ${neonButton(NeonColors.info, NeonColors.infoGlow)}
            padding: 8px 20px; font-size: 0.82rem; white-space: nowrap;
          ">SEARCH</button>
        </div>
        <div style="${neonPanel()}; display: flex; flex-direction: column; gap: 10px;">
          <div style="display: flex; justify-content: space-between; align-items: baseline; gap: 8px; flex-wrap: wrap;">
            <span style="${neonText(NeonColors.info, NeonColors.infoGlow)}; font-size: 0.95rem; font-weight: bold;">Latest Players</span>
            <span style="color: ${NeonColors.textDim}; font-size: 0.75rem;">Newest 20 accounts. Tap a player card to open details.</span>
          </div>
          <div id="recent-players" style="display: flex; flex-direction: column; gap: 8px;">
            <p style="color: ${NeonColors.textDim}; text-align: center; padding: 12px 0; font-size: 0.82rem;">
              Loading recent players...
            </p>
          </div>
        </div>
        <div style="${neonPanel()}; display: flex; flex-direction: column; gap: 10px;">
          <div style="display: flex; justify-content: space-between; align-items: baseline; gap: 8px; flex-wrap: wrap;">
            <span style="${neonText(NeonColors.info, NeonColors.infoGlow)}; font-size: 0.95rem; font-weight: bold;">Search Results</span>
            <span style="color: ${NeonColors.textDim}; font-size: 0.75rem;">Search by username, name, or Telegram ID.</span>
          </div>
          <div id="player-results" style="display: flex; flex-direction: column; gap: 8px;">
            <p style="color: ${NeonColors.textDim}; text-align: center; padding: 12px 0; font-size: 0.82rem;">
              Enter a search term to find players
            </p>
          </div>
        </div>
      </div>
    `

    this.bindSearchEvents()
    void this.loadRecentPlayers()
  }

  private bindSearchEvents(): void {
    const searchBtn = this.container.querySelector('#player-search-btn')
    const searchInput = this.container.querySelector('#player-search-input') as HTMLInputElement | null

    searchBtn?.addEventListener('click', () => {
      const query = searchInput?.value?.trim() ?? ''
      this.doSearch(query)
    })

    searchInput?.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        const query = searchInput.value.trim()
        this.doSearch(query)
      }
    })
  }

  private async doSearch(query: string): Promise<void> {
    const resultsArea = this.container.querySelector('#player-results')
    if (!resultsArea) return

    if (!query) {
      resultsArea.innerHTML = `<p style="color: ${NeonColors.textDim}; text-align: center; padding: 12px 0; font-size: 0.82rem;">Enter a search term to find players</p>`
      return
    }

    resultsArea.innerHTML = `<p style="color: ${NeonColors.textMuted}; text-align: center; padding: 20px 0;">Searching...</p>`

    const result = await this.fetchAdminResult(`/players?q=${encodeURIComponent(query)}`)
    if (!result.ok) {
      resultsArea.innerHTML = `<p style="color: ${NeonColors.danger}; text-align: center; padding: 20px 0;">Failed to search players${result.error ? `: ${this.escapeHtml(result.error)}` : ''}</p>`
      return
    }

    const data = result.data as { users: PlayerSummary[]; total: number }
    const users = data.users ?? []

    if (users.length === 0) {
      resultsArea.innerHTML = `<p style="color: ${NeonColors.textMuted}; text-align: center; padding: 20px 0;">No players found</p>`
      return
    }

    resultsArea.innerHTML = `
      <div style="color: ${NeonColors.textDim}; font-size: 0.75rem; margin-bottom: 4px;">
        ${data.total} result${data.total === 1 ? '' : 's'} found
      </div>
      ${users.map(u => this.renderPlayerCard(u)).join('')}
    `

    this.bindPlayerCardClicks()
  }

  private async loadRecentPlayers(): Promise<void> {
    const recentArea = this.container.querySelector('#recent-players')
    if (!recentArea) return

    const result = await this.fetchAdminResult('/users?limit=20')
    if (!result.ok) {
      recentArea.innerHTML = `<p style="color: ${NeonColors.danger}; text-align: center; padding: 12px 0; font-size: 0.82rem;">Failed to load latest players${result.error ? `: ${this.escapeHtml(result.error)}` : ''}</p>`
      return
    }

    const players = (result.data as RecentPlayerSummary[]) ?? []
    if (players.length <= 0) {
      recentArea.innerHTML = `<p style="color: ${NeonColors.textDim}; text-align: center; padding: 12px 0; font-size: 0.82rem;">No players yet</p>`
      return
    }

    recentArea.innerHTML = players.map((player) => this.renderPlayerCard(player, `${player.gamesPlayed} games`)).join('')
    this.bindPlayerCardClicks()
  }

  private renderPlayerCard(user: PlayerSummary, secondaryMeta?: string): string {
    const name = user.username || user.firstName || 'Unknown'
    return `
      <div class="player-card" data-player-id="${user.id}" style="
        ${neonPanel()}
        cursor: pointer;
        transition: border-color 0.2s;
        border: 1px solid ${NeonColors.textDim}33;
        display: flex; justify-content: space-between; align-items: center;
        gap: 12px; flex-wrap: wrap;
      ">
        <div style="display: flex; flex-direction: column; gap: 2px; min-width: 120px;">
          <span style="color: ${NeonColors.textBright}; font-weight: bold; font-size: 0.9rem;">${this.escapeHtml(name)}</span>
          <span style="color: ${NeonColors.textDim}; font-size: 0.75rem;">ID: ${user.telegramId}</span>
        </div>
        <div style="display: flex; gap: 16px; align-items: center;">
          <span style="color: ${NeonColors.gold}; font-size: 0.85rem; font-weight: bold;">${user.balance} stars</span>
          <span style="color: ${NeonColors.textDim}; font-size: 0.75rem;">${secondaryMeta ?? formatDate(user.createdAt)}</span>
        </div>
      </div>
    `
  }

  private bindPlayerCardClicks(): void {
    const cards = this.container.querySelectorAll('.player-card')
    cards.forEach(card => {
      card.addEventListener('click', () => {
        const playerId = (card as HTMLElement).dataset.playerId
        if (playerId) this.showPlayerDetail(playerId)
      })
    })
  }

  private async showPlayerDetail(playerId: string): Promise<void> {
    this.currentView = 'detail'
    this.selectedPlayerId = playerId

    this.container.innerHTML = `<p style="color: ${NeonColors.textMuted}; text-align: center; padding: 40px 0;">Loading player...</p>`

    const [playerResult, flagResult, historyResult] = await Promise.all([
      this.fetchAdminResult(`/players/${playerId}`),
      this.fetchAdminResult(`/players/${playerId}/flag`),
      this.fetchAdminResult(`/players/${playerId}/history`),
    ])

    if (!playerResult.ok) {
      this.container.innerHTML = `
        <p style="color: ${NeonColors.danger}; text-align: center; padding: 20px 0;">Failed to load player${playerResult.error ? `: ${this.escapeHtml(playerResult.error)}` : ''}</p>
        <div style="text-align: center;">
          <button id="back-to-search-btn" style="
            background: transparent; border: 1px solid ${NeonColors.textDim}; color: ${NeonColors.textMuted};
            padding: 6px 16px; border-radius: 6px; cursor: pointer; font-size: 0.8rem;
          ">Back to Search</button>
        </div>
      `
      this.bindBackButton()
      return
    }

    const player = playerResult.data as PlayerDetail
    const flag = flagResult.ok
      ? ((flagResult.data as FlagStatus) ?? { flagged: false, reason: '' })
      : { flagged: false, reason: '' }
    const history = historyResult.ok
      ? ((historyResult.data as PlayerHistory) ?? { violations: [], financials: [], bans: [] })
      : { violations: [], financials: [], bans: [] }

    this.renderPlayerDetailView(player, flag, history)
  }

  private renderPlayerDetailView(player: PlayerDetail, flag: FlagStatus, history: PlayerHistory): void {
    const name = player.username || player.firstName || 'Unknown'
    const isFlagged = flag.flagged
    const activeBans = history.bans.filter(b => !b.liftedAt && (!b.expiresAt || new Date(b.expiresAt) > new Date()))

    const txRows = (player.transactions ?? []).map(tx => `
      <tr style="border-bottom: 1px solid ${NeonColors.textDim}22;">
        <td style="padding: 6px 8px;">
          <span style="background: ${txTypeColor(tx.type)}22; color: ${txTypeColor(tx.type)};
            border: 1px solid ${txTypeColor(tx.type)}44; border-radius: 4px; padding: 1px 6px;
            font-size: 0.7rem; text-transform: uppercase;">${tx.type.replace(/_/g, ' ')}</span>
        </td>
        <td style="padding: 6px 8px; color: ${NeonColors.textBright}; text-align: right; font-weight: bold;">${tx.amount}</td>
        <td style="padding: 6px 8px; color: ${NeonColors.textDim}; font-size: 0.75rem;">${formatDateTime(tx.createdAt)}</td>
      </tr>
    `).join('')

    const gameRows = (player.games ?? []).map(g => `
      <tr style="border-bottom: 1px solid ${NeonColors.textDim}22;">
        <td style="padding: 6px 8px; color: ${NeonColors.textBright}; font-size: 0.82rem;">${formatDateTime(g.createdAt)}</td>
        <td style="padding: 6px 8px; color: ${NeonColors.textMuted}; text-align: right;">${g.duration != null ? `${Math.round(g.duration)}s` : '---'}</td>
      </tr>
    `).join('')

    const flagBtnColor = isFlagged ? NeonColors.success : NeonColors.danger
    const flagBtnGlow = isFlagged ? NeonColors.successGlow : NeonColors.dangerGlow
    const flagBtnLabel = isFlagged ? 'UNFLAG' : 'FLAG'

    this.container.innerHTML = `
      <div style="display: flex; flex-direction: column; gap: 14px;">
        <div>
          <button id="back-to-search-btn" style="
            background: transparent; border: none; color: ${NeonColors.info};
            cursor: pointer; font-size: 0.82rem; padding: 0; text-decoration: underline;
          ">Back to Search</button>
        </div>

        <!-- Player Info Card -->
        <div style="${neonPanel()}; display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 12px;">
          <div style="display: flex; flex-direction: column; gap: 4px;">
            <span style="${neonText(NeonColors.info, NeonColors.infoGlow)}; font-size: 1.1rem; font-weight: bold;">${this.escapeHtml(name)}</span>
            <span style="color: ${NeonColors.textDim}; font-size: 0.8rem;">Telegram ID: ${player.telegramId}</span>
            <span style="color: ${NeonColors.textDim}; font-size: 0.8rem;">Joined: ${formatDate(player.createdAt)}</span>
          </div>
          <div style="display: flex; flex-direction: column; gap: 4px; align-items: flex-end;">
            <span style="${neonText(NeonColors.gold, NeonColors.goldGlow)}; font-size: 1.2rem; font-weight: bold;">${player.balance} stars</span>
            <span style="color: ${NeonColors.textMuted}; font-size: 0.8rem;">K: ${player.kills} / D: ${player.deaths} / E: ${player.evacuations}</span>
            <span style="color: ${NeonColors.success}; font-size: 0.78rem;">Free games: ${player.freeGamesLeft}</span>
          </div>
        </div>

        <!-- Kick / Ban / Flag Actions -->
        <div style="${neonPanel()}; display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap;">
          <div style="display: flex; gap: 8px; align-items: center; flex-wrap: wrap;">
            <button id="kick-player-btn" style="
              ${neonButton(NeonColors.danger, NeonColors.dangerGlow)}
              padding: 6px 16px; font-size: 0.82rem;
            ">KICK</button>
            <button id="ban-player-btn" style="
              ${neonButton(NeonColors.danger, NeonColors.dangerGlow)}
              padding: 6px 16px; font-size: 0.82rem;
            ">BAN</button>
            <select id="ban-duration-select" style="
              ${INPUT_STYLE} padding: 6px 8px; font-size: 0.78rem; width: auto;
            ">
              <option value="1h">1 Hour</option>
              <option value="1d">1 Day</option>
              <option value="1w">1 Week</option>
              <option value="">Permanent</option>
            </select>
            ${activeBans.length > 0 ? `<button id="unban-player-btn" data-ban-id="${activeBans[0].id}" style="
              ${neonButton(NeonColors.success, NeonColors.successGlow)}
              padding: 6px 16px; font-size: 0.82rem;
            ">UNBAN</button>` : ''}
          </div>
          <div style="display: flex; gap: 8px; align-items: center;">
            <span style="color: ${isFlagged ? NeonColors.danger : NeonColors.success}; font-weight: bold; font-size: 0.82rem;">
              ${isFlagged ? 'FLAGGED' : 'CLEAN'}
            </span>
            ${isFlagged && flag.reason ? `<span style="color: ${NeonColors.textDim}; font-size: 0.72rem;">(${this.escapeHtml(flag.reason)})</span>` : ''}
            <button id="flag-player-btn" data-flagged="${isFlagged}" style="
              ${neonButton(flagBtnColor, flagBtnGlow)}
              padding: 6px 16px; font-size: 0.82rem;
            ">${flagBtnLabel}</button>
          </div>
        </div>

        <!-- Balance / Trial tools -->
        <div style="${neonPanel()}; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;">
          <div style="display: flex; gap: 8px; align-items: center; flex-wrap: wrap;">
            <button id="full-refund-btn" style="
              ${neonButton(NeonColors.gold, NeonColors.goldGlow)}
              padding: 6px 16px; font-size: 0.82rem;
            ">FULL REFUND</button>
            <span style="color: ${NeonColors.textDim}; font-size: 0.75rem;">
              Refund all spent real stars (stakes + respawns)
            </span>
          </div>
          <div style="display: flex; gap: 8px; align-items: center;">
            <input id="free-games-amount-input" type="number" min="1" step="1" value="3" style="
              ${INPUT_STYLE}
              width: 72px; padding: 6px 8px; font-size: 0.78rem; text-align: center;
            " />
            <button id="grant-free-games-btn" style="
              ${neonButton(NeonColors.success, NeonColors.successGlow)}
              padding: 6px 16px; font-size: 0.82rem;
            ">GRANT FREE GAMES</button>
          </div>
        </div>

        <!-- Active Bans -->
        ${activeBans.length > 0 ? `
        <div style="${neonPanel()}; border-color: ${NeonColors.danger}44;">
          <h3 style="margin: 0 0 8px 0; font-size: 0.9rem; ${neonText(NeonColors.danger, NeonColors.dangerGlow)}">
            Active Bans (${activeBans.length})
          </h3>
          ${activeBans.map(b => `
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 6px 0; border-bottom: 1px solid ${NeonColors.textDim}22;">
              <div>
                <span style="color: ${NeonColors.textBright}; font-size: 0.82rem;">${this.escapeHtml(b.reason)}</span>
                <span style="color: ${NeonColors.textDim}; font-size: 0.72rem; margin-left: 8px;">
                  ${b.expiresAt ? `Expires: ${formatDateTime(b.expiresAt)}` : 'Permanent'}
                </span>
              </div>
              <button class="unban-btn" data-ban-id="${b.id}" style="
                background: transparent; border: 1px solid ${NeonColors.success}; color: ${NeonColors.success};
                border-radius: 4px; padding: 2px 10px; font-size: 0.72rem; cursor: pointer;
              ">Lift</button>
            </div>
          `).join('')}
        </div>` : ''}

        <!-- Violation History -->
        <div style="${neonPanel()};">
          <h3 style="margin: 0 0 8px 0; font-size: 0.9rem; ${neonText(NeonColors.primary, NeonColors.primaryGlow)}">
            Admin & Moderation History (${history.violations.length})
          </h3>
          ${history.violations.length > 0 ? `
            <div style="max-height: 180px; overflow-y: auto;">
              ${history.violations.slice(0, 20).map(v => `
                <div style="display: flex; justify-content: space-between; gap: 10px; padding: 4px 0; border-bottom: 1px solid ${NeonColors.textDim}15;">
                  <div style="display: flex; flex-direction: column; gap: 2px;">
                    <span style="color: ${NeonColors.primary}; font-size: 0.78rem; text-transform: uppercase;">${this.escapeHtml(v.type)}</span>
                    <span style="color: ${NeonColors.textMuted}; font-size: 0.72rem;">${this.escapeHtml(this.describeHistoryEvent(v))}</span>
                  </div>
                  <span style="color: ${NeonColors.textDim}; font-size: 0.72rem; white-space: nowrap;">${formatDateTime(v.createdAt)}</span>
                </div>
              `).join('')}
            </div>
          ` : `<p style="color: ${NeonColors.textDim}; font-size: 0.8rem;">No violations</p>`}
        </div>

        <!-- Ban History -->
        ${history.bans.length > 0 ? `
        <div style="${neonPanel()};">
          <h3 style="margin: 0 0 8px 0; font-size: 0.9rem; ${neonText(NeonColors.danger, NeonColors.dangerGlow)}">
            Ban History (${history.bans.length})
          </h3>
          <div style="max-height: 180px; overflow-y: auto;">
            ${history.bans.map(b => `
              <div style="display: flex; justify-content: space-between; align-items: center; padding: 4px 0; border-bottom: 1px solid ${NeonColors.textDim}15;">
                <div>
                  <span style="color: ${NeonColors.textBright}; font-size: 0.78rem;">${this.escapeHtml(b.reason)}</span>
                  <span style="color: ${b.liftedAt ? NeonColors.success : NeonColors.danger}; font-size: 0.7rem; margin-left: 6px;">
                    ${b.liftedAt ? 'Lifted' : b.expiresAt ? (new Date(b.expiresAt) < new Date() ? 'Expired' : 'Active') : 'Permanent'}
                  </span>
                </div>
                <span style="color: ${NeonColors.textDim}; font-size: 0.72rem;">${formatDateTime(b.createdAt)}</span>
              </div>
            `).join('')}
          </div>
        </div>` : ''}

        <!-- Transactions -->
        <div style="${neonPanel()};">
          <h3 style="margin: 0 0 10px 0; font-size: 0.9rem; ${neonText(NeonColors.gold, NeonColors.goldGlow)}">
            Recent Transactions (${(player.transactions ?? []).length})
          </h3>
          ${(player.transactions ?? []).length > 0 ? `
            <div style="max-height: 250px; overflow-y: auto;">
              <table style="width: 100%; border-collapse: collapse;">
                <thead>
                  <tr style="border-bottom: 1px solid ${NeonColors.textDim}55;">
                    <th style="padding: 6px 8px; text-align: left; color: ${NeonColors.textMuted}; font-size: 0.75rem;">Type</th>
                    <th style="padding: 6px 8px; text-align: right; color: ${NeonColors.textMuted}; font-size: 0.75rem;">Amount</th>
                    <th style="padding: 6px 8px; text-align: left; color: ${NeonColors.textMuted}; font-size: 0.75rem;">Date</th>
                  </tr>
                </thead>
                <tbody>${txRows}</tbody>
              </table>
            </div>
          ` : `<p style="color: ${NeonColors.textDim}; font-size: 0.8rem;">No transactions</p>`}
        </div>

        <!-- Games -->
        <div style="${neonPanel()};">
          <h3 style="margin: 0 0 10px 0; font-size: 0.9rem; ${neonText(NeonColors.portal, NeonColors.portalGlow)}">
            Recent Games (${(player.games ?? []).length})
          </h3>
          ${(player.games ?? []).length > 0 ? `
            <div style="max-height: 200px; overflow-y: auto;">
              <table style="width: 100%; border-collapse: collapse;">
                <thead>
                  <tr style="border-bottom: 1px solid ${NeonColors.textDim}55;">
                    <th style="padding: 6px 8px; text-align: left; color: ${NeonColors.textMuted}; font-size: 0.75rem;">Date</th>
                    <th style="padding: 6px 8px; text-align: right; color: ${NeonColors.textMuted}; font-size: 0.75rem;">Duration</th>
                  </tr>
                </thead>
                <tbody>${gameRows}</tbody>
              </table>
            </div>
          ` : `<p style="color: ${NeonColors.textDim}; font-size: 0.8rem;">No games</p>`}
        </div>
      </div>
    `

    this.bindBackButton()
    this.bindFlagButton()
    this.bindKickBanButtons()
  }

  private bindBackButton(): void {
    const backBtn = this.container.querySelector('#back-to-search-btn')
    backBtn?.addEventListener('click', () => {
      this.renderSearchView()
    })
  }

  private bindFlagButton(): void {
    const flagBtn = this.container.querySelector('#flag-player-btn') as HTMLButtonElement | null
    if (!flagBtn || !this.selectedPlayerId) return

    flagBtn.addEventListener('click', async () => {
      const currentlyFlagged = flagBtn.dataset.flagged === 'true'
      const newFlagged = !currentlyFlagged

      let reason: string | undefined
      if (newFlagged) {
        reason = window.prompt('Enter reason for flagging this player:') ?? undefined
        if (reason === undefined) return // User cancelled
      }

      try {
        const resp = await fetch(`/api/admin/players/${this.selectedPlayerId}/flag`, {
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + this.token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ flagged: newFlagged, reason }),
        })

        const json = await resp.json()
        if (json.success) {
          this.showToast(newFlagged ? 'Player flagged' : 'Player unflagged', 'success')
          // Refresh detail view
          if (this.selectedPlayerId) {
            this.showPlayerDetail(this.selectedPlayerId)
          }
        } else {
          this.showToast(json.error ?? 'Failed to flag player', 'error')
        }
      } catch {
        this.showToast('Network error', 'error')
      }
    })
  }

  private bindKickBanButtons(): void {
    if (!this.selectedPlayerId) return
    const playerId = this.selectedPlayerId

    // Kick button
    this.container.querySelector('#kick-player-btn')?.addEventListener('click', async () => {
      const reason = window.prompt('Reason for kicking this player:')
      if (!reason) return
      const result = await this.postAdmin(`/players/${playerId}/kick`, { reason })
      if (result) {
        this.showToast('Player kicked', 'success')
        this.showPlayerDetail(playerId)
      } else {
        this.showToast('Failed to kick player', 'error')
      }
    })

    // Ban button
    this.container.querySelector('#ban-player-btn')?.addEventListener('click', async () => {
      const reason = window.prompt('Reason for banning this player:')
      if (!reason) return
      const select = this.container.querySelector('#ban-duration-select') as HTMLSelectElement | null
      const duration = select?.value || null
      const result = await this.postAdmin(`/players/${playerId}/ban`, { reason, duration })
      if (result) {
        this.showToast('Player banned', 'success')
        this.showPlayerDetail(playerId)
      } else {
        this.showToast('Failed to ban player', 'error')
      }
    })

    // Unban button (top-level)
    this.container.querySelector('#unban-player-btn')?.addEventListener('click', async () => {
      const banId = (this.container.querySelector('#unban-player-btn') as HTMLElement)?.dataset.banId
      if (!banId) return
      const result = await this.deleteAdmin(`/players/${playerId}/ban/${banId}`)
      if (result) {
        this.showToast('Ban lifted', 'success')
        this.showPlayerDetail(playerId)
      } else {
        this.showToast('Failed to unban player', 'error')
      }
    })

    // Individual ban lift buttons
    this.container.querySelectorAll('.unban-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        const banId = (btn as HTMLElement).dataset.banId
        if (!banId) return
        const result = await this.deleteAdmin(`/players/${playerId}/ban/${banId}`)
        if (result) {
          this.showToast('Ban lifted', 'success')
          this.showPlayerDetail(playerId)
        } else {
          this.showToast('Failed to unban player', 'error')
        }
      })
    })

    this.container.querySelector('#full-refund-btn')?.addEventListener('click', async () => {
      if (!window.confirm('Run full refund for this player (all spent real stars)?')) return

      try {
        const resp = await fetch(`/api/admin/players/${playerId}/full-refund`, {
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + this.token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ reason: 'bug compensation' }),
        })
        const json = await resp.json()
        if (json.success) {
          const refunded = Number(json.data?.refundedStars ?? 0)
          this.showToast(`Refunded ${refunded} stars`, 'success')
          this.showPlayerDetail(playerId)
        } else {
          this.showToast(json.error ?? 'Failed to run full refund', 'error')
        }
      } catch {
        this.showToast('Network error', 'error')
      }
    })

    this.container.querySelector('#grant-free-games-btn')?.addEventListener('click', async () => {
      const amountInput = this.container.querySelector('#free-games-amount-input') as HTMLInputElement | null
      const amount = Number.parseInt(amountInput?.value ?? '0', 10)
      if (!Number.isInteger(amount) || amount <= 0) {
        this.showToast('Enter a positive integer amount', 'error')
        return
      }

      try {
        const resp = await fetch(`/api/admin/players/${playerId}/adjust-free-games`, {
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + this.token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ amount, reason: 'manual test games grant' }),
        })
        const json = await resp.json()
        if (json.success) {
          const nextValue = Number(json.data?.freeGamesLeft ?? 0)
          this.showToast(`Granted +${amount} test games (now ${nextValue})`, 'success')
          this.showPlayerDetail(playerId)
        } else {
          this.showToast(json.error ?? 'Failed to grant test games', 'error')
        }
      } catch {
        this.showToast('Network error', 'error')
      }
    })
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    const existing = document.querySelector('.admin-toast')
    if (existing) existing.remove()

    const color = type === 'success' ? NeonColors.success : NeonColors.danger
    const toast = document.createElement('div')
    toast.className = 'admin-toast'
    toast.style.cssText = `
      position: absolute; bottom: 24px; right: 24px;
      background: ${NeonColors.bgDark}; border: 1px solid ${color};
      color: ${color}; border-radius: 8px; padding: 10px 20px;
      font-size: 0.85rem; box-shadow: 0 0 16px ${color}44;
      z-index: 9999; opacity: 0; transition: opacity 0.3s ease;
    `
    toast.textContent = message
    mountToAppShell(toast)

    requestAnimationFrame(() => { toast.style.opacity = '1' })
    setTimeout(() => {
      toast.style.opacity = '0'
      setTimeout(() => toast.remove(), 300)
    }, 3000)
  }

  private escapeHtml(str: string): string {
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }

  private describeHistoryEvent(event: ViolationRecord): string {
    const data = (event.data ?? {}) as Record<string, unknown>

    switch (event.type) {
      case 'admin_free_games_adjust':
        return `${data.amount ?? '?'} free games, reason: ${data.reason ?? 'n/a'}`
      case 'admin_flag':
        return `${data.flagged ? 'flagged' : 'unflagged'}${data.reason ? `, reason: ${data.reason}` : ''}`
      case 'admin_ban':
        return `${data.reason ?? 'ban issued'}`
      case 'player_kick':
        return `${data.reason ?? 'kicked by admin'}`
      case 'violation':
        return `${data.reason ?? data.violationType ?? 'rule violation'}`
      default:
        return 'admin event'
    }
  }

  private async fetchAdminResult(path: string): Promise<{ ok: true; data: unknown } | { ok: false; error: string | null }> {
    try {
      const resp = await fetch('/api/admin' + path, {
        headers: { Authorization: 'Bearer ' + this.token },
      })
      const json = await resp.json()
      if (!resp.ok || !json?.success) {
        return { ok: false, error: json?.error ?? `HTTP ${resp.status}` }
      }
      return { ok: true, data: json.data ?? null }
    } catch {
      return { ok: false, error: 'network error' }
    }
  }

  private async postAdmin(path: string, body: unknown): Promise<boolean> {
    try {
      const resp = await fetch('/api/admin' + path, {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + this.token, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await resp.json()
      return !!json.success
    } catch {
      return false
    }
  }

  private async deleteAdmin(path: string): Promise<boolean> {
    try {
      const resp = await fetch('/api/admin' + path, {
        method: 'DELETE',
        headers: { Authorization: 'Bearer ' + this.token },
      })
      const json = await resp.json()
      return !!json.success
    } catch {
      return false
    }
  }
}
